<template>
    <span
        v-if="disableLink"
        class="eiwwqkts _noSelect"
        @contextmenu.stop.capture="showPreviewCard"
        :class="{
            cat: user.avatar_decoration === 'CatEars',
            square: squareAvatars,
        }"
        :style="{ color }"
        v-tooltip="magFormatAcct(user)"
        @click="onClick"
    >
        <img class="inner" :src="url" decoding="async" />
    </span>
    <MkA
        v-else
        class="eiwwqkts _noSelect"
        :context-behavior="
            props.disablePreview
                ? { type: 'native' }
                : {
                      type: 'userCard',
                      username: user.username,
                      host: user.host,
                  }
        "
        :class="{
            cat: user.avatar_decoration === 'CatEars' || false,
            square: squareAvatars,
        }"
        :style="{ color }"
        :to="UserModel(user).userPageUrl"
        v-tooltip="magFormatAcct(user)"
        :target="target"
        @click.stop
    >
        <img
            width="32"
            height="32"
            class="inner"
            :class="{ hidden }"
            :src="url"
            decoding="async"
        />
    </MkA>
</template>

<script lang="ts">
import { defineAsyncComponent } from "vue";

const MagUserPreview = defineAsyncComponent(
    () => import("@/components/MagUserPreview.vue"),
);
</script>
<script lang="ts" setup>
import { computed } from "vue";
import { getStaticImageUrl } from "@/scripts/get-static-image-url";
import { extractAvgColorFromBlurhash } from "@/scripts/blurhash";
import { packed } from "magnetar-common";
import { magFormatAcct } from "@/scripts-mag/mag-util";
import { UserModel } from "@/model/user";
import { useSettingsStore } from "@/store/settings-store";
import { storeToRefs } from "pinia";
import { refProp } from "@/composables/reactive";

import { openModal } from "@/composables/modal";
import MkImgWithBlurhash from "@/components/primitives/MkImgWithBlurhash.vue";

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const disableShowingAnimatedImages = refProp(
    settings,
    "disableShowingAnimatedImages",
);
const squareAvatars = refProp(settings, "squareAvatars");
const advancedMutes = refProp(settings, "advancedMutes");

const props = withDefaults(
    defineProps<{
        user: packed.PackUserBase;
        target?: string | null;
        disableLink?: boolean;
        disablePreview?: boolean;
    }>(),
    {
        target: null,
        disableLink: false,
        disablePreview: false,
    },
);

const emit = defineEmits<{
    (ev: "click", v: MouseEvent): void;
}>();

function showPreviewCard(e: MouseEvent) {
    if (!props.disablePreview) {
        openModal(
            {
                dismissable: true,
                lowBackdrop: true,
                transformOrigin: {
                    x: e.x,
                    y: e.y,
                },
                anchor: {
                    kind: "around-point",
                    x: e.x,
                    y: e.y,
                    align: "start",
                    position: "bottom",
                },
            },
            MagUserPreview,
            ({ onClose }) => ({
                onClosed: onClose,
                userTag: props.user,
            }),
        );

        e.preventDefault();
    }

    e.stopPropagation();
}

const color = computed(() =>
    extractAvgColorFromBlurhash(props.user.avatar_blurhash),
);

const hidden = computed(() =>
    advancedMutes.value.rules.some(
        (u) => u.user === props.user.acct && u.hideAvatar,
    ),
);

const url = computed(() =>
    disableShowingAnimatedImages.value
        ? getStaticImageUrl(props.user.avatar_url)
        : props.user.avatar_url,
);

function onClick(ev: MouseEvent) {
    emit("click", ev);
}
</script>

<style lang="scss" scoped>
@keyframes earwiggleleft {
    from {
        transform: rotate(37.6deg) skew(30deg);
    }
    25% {
        transform: rotate(10deg) skew(30deg);
    }
    50% {
        transform: rotate(20deg) skew(30deg);
    }
    75% {
        transform: rotate(0deg) skew(30deg);
    }
    to {
        transform: rotate(37.6deg) skew(30deg);
    }
}

@keyframes earwiggleright {
    from {
        transform: rotate(-37.6deg) skew(-30deg);
    }
    30% {
        transform: rotate(-10deg) skew(-30deg);
    }
    55% {
        transform: rotate(-20deg) skew(-30deg);
    }
    75% {
        transform: rotate(0deg) skew(-30deg);
    }
    to {
        transform: rotate(-37.6deg) skew(-30deg);
    }
}

.eiwwqkts {
    position: relative;
    display: inline-block;
    vertical-align: bottom;
    flex-shrink: 0;
    border-radius: 100%;
    line-height: 16px;

    &:has(> .inner.hidden) {
        background-color: currentColor;
    }

    > .inner {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        top: 0;
        border-radius: 100%;
        z-index: 1;
        overflow: hidden;
        object-fit: cover;
        width: 100%;
        height: 100%;

        &.hidden {
            visibility: hidden;
        }
    }

    > .indicator {
        position: absolute;
        z-index: 1;
        bottom: 0;
        left: 0;
        width: 18%;
        height: 18%;
    }

    &.square {
        border-radius: 20%;

        > .inner {
            border-radius: 20%;
        }
    }

    &.cat {
        &:before,
        &:after {
            background: #ebbcba;
            border: solid 4px currentColor;
            box-sizing: border-box;
            content: "";
            display: inline-block;
            height: 50%;
            width: 50%;
        }

        &:before {
            border-radius: 0 75% 75%;
            transform: rotate(37.5deg) skew(30deg);
        }

        &:after {
            border-radius: 75% 0 75% 75%;
            transform: rotate(-37.5deg) skew(-30deg);
        }

        &:hover {
            &:before {
                animation: earwiggleleft 1s infinite;
            }

            &:after {
                animation: earwiggleright 1s infinite;
            }
        }
    }
}
</style>
